/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useRef } from 'react'
import { FieldArray, FieldArrayRenderProps } from 'formik'
import { Select } from '../../../../../components/Select'
import { useServiceContext } from '../../../../../root'
import SelectUserGroup from './SelectUserGroup'
import SelectStepLabel from './SelectStepLabel'
import { WF_STEP_PROCESSING_TYPE, type WfStepProcessingType } from './constants'

interface UserGroup {
  value: number
  label: string
}

export interface StepConfig {
  processingType: WfStepProcessingType
  taskTeamStepLabel: any // Can be string, null, or option object
  nofAllValue: string
  usersGroups: UserGroup[]
}

interface UserGroupStepsProps {
  name: string
  values: StepConfig[]
  nofallTxt?: string
  labelTxt?: string
  isFromManageWFModals?: boolean
  disabled?: boolean
  showError?: boolean
  errorMessage?: string
}

const UserGroupSteps: React.FC<UserGroupStepsProps> = ({
  name,
  values,
  nofallTxt,
  labelTxt,
  isFromManageWFModals = false,
  disabled = false,
  showError = false,
  errorMessage
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const arrayHelpersRef = useRef<FieldArrayRenderProps | null>(null)
  const initializedRef = useRef(false)

  const processingTypeOptions = [
    { value: WF_STEP_PROCESSING_TYPE.SERIAL, label: translate('ONE_AT_A_TIME') },
    { value: WF_STEP_PROCESSING_TYPE.PARALLEL, label: translate('ALL_AT_ONCE') },
    { value: WF_STEP_PROCESSING_TYPE.N_OF_ALL, label: translate('N_OF_ALL') }
  ]

  // Auto-add first step if empty (matching legacy behavior)
  useEffect(() => {
    if (!initializedRef.current && arrayHelpersRef.current && values.length < 1) {
      arrayHelpersRef.current.push({
        processingType: WF_STEP_PROCESSING_TYPE.SERIAL,
        taskTeamStepLabel: null,
        nofAllValue: '',
        usersGroups: []
      })
      initializedRef.current = true
    }
  }, [values.length])

  const renderSteps = (arrayHelpers: FieldArrayRenderProps) => {
    const steps = values || []

    // Store arrayHelpers ref for useEffect
    arrayHelpersRef.current = arrayHelpers

    return (
      <div className="form-group">
        {showError && errorMessage && <div className="error-message text-danger mb-3">{errorMessage}</div>}

        <label className="control-label">{translate(labelTxt || 'WF_STEP.CUSTOM_LABEL')}</label>

        <div className="custom-approvers-table">
          {/* Table Header */}
          {steps.length > 0 && (
            <div className="table-header">
              <span className="h steps ta-center">{translate('STEP')}</span>
              <span className="h customlabel">{translate('CR_CUSTOM_STEP_LABEL')}</span>
              <span className="h usersgroups">{translate('USERS_GROUPS')}</span>
              <span className="h order">{translate('ORDER')}</span>
            </div>
          )}

          {/* Table Rows */}
          {steps.map((step, index) => (
            <div key={index} className="table-row py5">
              <span className="h steps ta-center py5">{index + 1}</span>

              {/* Custom Label */}
              <span className="h customlabel">
                <SelectStepLabel
                  name={`${name}[${index}].taskTeamStepLabel`}
                  value={step.taskTeamStepLabel}
                  onChange={(option: any) => {
                    arrayHelpers.replace(index, {
                      ...step,
                      taskTeamStepLabel: option // Store the entire option object, not just value
                    })
                  }}
                  isDisabled={disabled}
                />
              </span>

              {/* Users/Groups */}
              <span className="h usersgroups">
                <SelectUserGroup
                  name={`${name}[${index}].usersGroups`}
                  value={step.usersGroups}
                  onChange={(options: any) => {
                    arrayHelpers.replace(index, {
                      ...step,
                      usersGroups: options || []
                    })
                  }}
                  isMulti={true}
                  isDisabled={disabled}
                />
              </span>

              {/* Processing Type (Order) */}
              <span className="h order">
                <Select
                  name={`${name}[${index}].processingType`}
                  value={step.processingType}
                  options={processingTypeOptions}
                  onChange={(option: any) => {
                    const newProcessingType = option?.value || WF_STEP_PROCESSING_TYPE.SERIAL
                    const updatedStep = {
                      ...step,
                      processingType: newProcessingType
                    }
                    // Clear nofAllValue if not N_OF_ALL
                    if (newProcessingType !== WF_STEP_PROCESSING_TYPE.N_OF_ALL && isFromManageWFModals) {
                      updatedStep.nofAllValue = ''
                    }
                    arrayHelpers.replace(index, updatedStep)
                  }}
                  isClearable={false}
                  searchable={true}
                  showNewOptionAtTop={true}
                  isDisabled={disabled}
                  id="795f0a8a-f8b3-47d8-81ca-b3b3ab065e27"
                />

                {/* N of All Value Input */}
                {step.processingType === WF_STEP_PROCESSING_TYPE.N_OF_ALL && (
                  <div className="nofall-wrapper hform-group">
                    <input
                      type="number"
                      className="md-input nofall-input"
                      name={`${name}[${index}].nofAllValue`}
                      value={step.nofAllValue}
                      onChange={(e) => {
                        arrayHelpers.replace(index, {
                          ...step,
                          nofAllValue: e.target.value
                        })
                      }}
                      disabled={disabled}
                      readOnly={disabled}
                    />
                    {translate(nofallTxt || 'BROWSER_FILE_SETTINGS_CR_APPROVERS_INFO_LABEL')}
                  </div>
                )}
              </span>

              {/* Remove Button */}
              {!disabled && (
                <a
                  className="close"
                  onClick={(e) => {
                    e.preventDefault()
                    arrayHelpers.remove(index)
                  }}
                ></a>
              )}
            </div>
          ))}
        </div>

        {/* Add Step Button */}
        {!disabled && (
          <button
            type="button"
            className="btn btn-chita btn-add-step btn-second at_btnAddStepApprover"
            onClick={() => {
              arrayHelpers.push({
                processingType: WF_STEP_PROCESSING_TYPE.SERIAL,
                taskTeamStepLabel: null,
                nofAllValue: '',
                usersGroups: []
              })
            }}
          >
            {translate('ADD_STEP')}
          </button>
        )}
      </div>
    )
  }

  return <FieldArray name={name}>{renderSteps}</FieldArray>
}

export default UserGroupSteps
